import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Typography, Empty, Spin, Input, Avatar, Card } from 'antd';
import {
  ArrowLeftOutlined, HistoryOutlined, SolutionOutlined, DeleteOutlined,
  CheckCircleOutlined, TeamOutlined, SearchOutlined, UserOutlined,
  ClockCircleOutlined, CalendarOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import { batchActions } from '../../actions/batchActions';

const { Text, Title } = Typography;

// ─── Event Config ────────────────────────────────────────────────────────────
interface EventCfg {
  label: string;
  icon: React.ReactNode;
  color: string;
  bg: string;
  border: string;
  category: string;
  description: (r: any) => string;
}

const EVENT_CONFIG: Record<string, EventCfg> = {
  BATCH_ASSIGNED: {
    label: 'Instructor Assigned',
    icon: <SolutionOutlined />,
    color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe',
    category: 'assignments',
    description: (r) => {
      const name = r.instructorDetails
        ? `${r.instructorDetails.firstName || ''} ${r.instructorDetails.lastName || ''}`.trim()
        : 'An instructor';
      return `${name} was assigned to this batch.`;
    },
  },
  BATCH_REMOVED: {
    label: 'Instructor Removed',
    icon: <DeleteOutlined />,
    color: '#be123c', bg: '#fff1f2', border: '#fecdd3',
    category: 'assignments',
    description: (r) => {
      const name = r.instructorDetails
        ? `${r.instructorDetails.firstName || ''} ${r.instructorDetails.lastName || ''}`.trim()
        : 'An instructor';
      return `${name} was removed from this batch.`;
    },
  },
  BATCH_COMPLETED: {
    label: 'Batch Completed',
    icon: <CheckCircleOutlined />,
    color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0',
    category: 'batch_updates',
    description: () => 'This batch has been marked as completed.',
  },
  SESSION_CONDUCTED: {
    label: 'Attendance Marked',
    icon: <TeamOutlined />,
    color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0',
    category: 'attendance',
    description: (r) => {
      const present = r.attendanceRecords?.filter((a: any) => a.status === 'PRESENT').length || 0;
      const total   = r.attendanceRecords?.length || r.studentsCount || 0;
      return `Session conducted. ${present} of ${total} students marked present.`;
    },
  },
};

const FILTER_CHIPS = [
  { key: 'all',          label: 'All' },
  { key: 'assignments',  label: 'Assignments' },
  { key: 'attendance',   label: 'Attendance' },
  { key: 'batch_updates',label: 'Batch Updates' },
  // { key: 'students',     label: 'Students' },
  // { key: 'profile',      label: 'Profile' },
  // { key: 'leave',        label: 'Leave' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
const fmtRelative = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)  return `${d}d ago`;
  return fmtDate(iso);
};

const getInstructorName = (r: any) =>
  r.instructorDetails
    ? `${r.instructorDetails.firstName || ''} ${r.instructorDetails.lastName || ''}`.trim()
    : null;

// ─── TimelineItem ─────────────────────────────────────────────────────────────
const TimelineItem: React.FC<{ record: any; isLast: boolean }> = ({ record, isLast }) => {
  const cfg = EVENT_CONFIG[record.type] || {
    label: record.type || 'Activity',
    icon: <HistoryOutlined />,
    color: '#64748b', bg: '#f8fafc', border: '#e2e8f0',
    description: () => 'An activity was recorded.',
  };
  const instructorName = getInstructorName(record);
  const hasAttendance  = record.attendanceRecords?.length > 0;
  const presentCount   = record.attendanceRecords?.filter((a: any) => a.status === 'PRESENT').length || 0;
  const absentCount    = record.attendanceRecords?.filter((a: any) => a.status === 'ABSENT').length || 0;

  return (
    <div className="bh2-item">
      {/* Spine */}
      <div className="bh2-spine">
        <div className="bh2-node" style={{ background: cfg.bg, border: `2px solid ${cfg.border}`, color: cfg.color }}>
          {cfg.icon}
        </div>
        {!isLast && <div className="bh2-spine-line" />}
      </div>

      {/* Card */}
      <div className="bh2-card">
        <div className="bh2-card-head">
          <div className="bh2-card-head-left">
            <span className="bh2-event-tag" style={{ color: cfg.color, borderColor: cfg.border }}>
              {cfg.label.toUpperCase()}
            </span>
          </div>
          {record.createdAt && (
            <div className="bh2-timestamp">
              <ClockCircleOutlined className="bh2-ts-icon" />
              <span className="bh2-ts-rel">{fmtRelative(record.createdAt)}</span>
              <span className="bh2-ts-full">{fmtDate(record.createdAt)} · {fmtTime(record.createdAt)}</span>
            </div>
          )}
        </div>

        <p className="bh2-desc">{(cfg.description as Function)(record)}</p>

        {/* Actor row */}
        {instructorName && (
          <div className="bh2-actor">
            <Avatar size={22} icon={<UserOutlined />} className="bh2-actor-avatar" />
            <span className="bh2-actor-name">{instructorName}</span>
            <span className="bh2-actor-role">Instructor</span>
          </div>
        )}

        {/* Attendance pills */}
        {hasAttendance && (
          <div className="bh2-attendance">
            <div className="bh2-att-summary">
              <span className="bh2-att-chip bh2-att-chip--present"><span className="bh2-att-dot bh2-att-dot--present" />{presentCount} Present</span>
              <span className="bh2-att-chip bh2-att-chip--absent"><span className="bh2-att-dot bh2-att-dot--absent" />{absentCount} Absent</span>
              <span className="bh2-att-chip bh2-att-chip--total">{record.attendanceRecords.length} Total</span>
            </div>
            <div className="bh2-student-pills">
              {record.attendanceRecords.map((ar: any, i: number) => {
                const isPresent = ar.status === 'PRESENT';
                return (
                  <span key={i} className={`bh2-student-pill bh2-student-pill--${isPresent ? 'present' : 'absent'}`}>
                    <span className={`bh2-att-dot bh2-att-dot--${isPresent ? 'present' : 'absent'}`} />
                    {ar.studentName || 'Student'}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── SummaryCard ──────────────────────────────────────────────────────────────
const SummaryCard: React.FC<{ list: any[] }> = ({ list }) => {
  const first  = list.length ? list[list.length - 1] : null;
  const latest = list.length ? list[0] : null;
  const instructorsSet = new Set(list.map((r) => r.instructorId?.toString()).filter(Boolean));

  const rows = [
    { label: 'First Activity',    value: first?.createdAt  ? fmtDate(first.createdAt)  : '—', icon: <CalendarOutlined /> },
    { label: 'Latest Activity',   value: latest?.createdAt ? fmtDate(latest.createdAt) : '—', icon: <ClockCircleOutlined /> },
    { label: 'Total Activities',  value: list.length,                                          icon: <HistoryOutlined /> },
    { label: 'Unique Instructors',value: instructorsSet.size,                                  icon: <UserOutlined /> },
  ];

  return (
    <Card className="bh2-sidebar-card" size="small">
      <div className="bh2-sidebar-card-title"><ThunderboltOutlined /> Summary</div>
      <div className="bh2-summary-rows">
        {rows.map((r) => (
          <div key={r.label} className="bh2-summary-row">
            <span className="bh2-summary-icon">{r.icon}</span>
            <span className="bh2-summary-label">{r.label}</span>
            <span className="bh2-summary-value">{r.value}</span>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ─── ActorsCard ───────────────────────────────────────────────────────────────
const ActorsCard: React.FC<{ list: any[] }> = ({ list }) => {
  const actorMap: Record<string, { name: string; count: number }> = {};
  list.forEach((r) => {
    const name = getInstructorName(r);
    const key  = r.instructorId?.toString();
    if (name && key) {
      if (!actorMap[key]) actorMap[key] = { name, count: 0 };
      actorMap[key].count++;
    }
  });
  const actors = Object.values(actorMap).sort((a, b) => b.count - a.count).slice(0, 5);

  return (
    <Card className="bh2-sidebar-card" size="small">
      <div className="bh2-sidebar-card-title"><UserOutlined /> Instructors</div>
      {actors.length === 0
        ? <Text type="secondary" style={{ fontSize: 12 }}>No actors recorded</Text>
        : actors.map((a, i) => (
          <div key={i} className="bh2-actor-row">
            <Avatar size={28} icon={<UserOutlined />} className="bh2-actor-av" />
            <div className="bh2-actor-info">
              <span className="bh2-actor-nm">{a.name}</span>
              <span className="bh2-actor-rl">Instructor</span>
            </div>
            <span className="bh2-actor-count">{a.count}</span>
          </div>
        ))
      }
    </Card>
  );
};

// ─── EventTypesCard ───────────────────────────────────────────────────────────
const EventTypesCard: React.FC<{ list: any[] }> = ({ list }) => {
  const countMap: Record<string, number> = {};
  list.forEach((r) => { countMap[r.type] = (countMap[r.type] || 0) + 1; });
  const entries = Object.entries(countMap).sort((a, b) => b[1] - a[1]);
  const total   = list.length || 1;

  return (
    <Card className="bh2-sidebar-card" size="small">
      <div className="bh2-sidebar-card-title"><HistoryOutlined /> Event Types</div>
      {entries.length === 0
        ? <Text type="secondary" style={{ fontSize: 12 }}>No events</Text>
        : entries.map(([type, count]) => {
          const cfg = EVENT_CONFIG[type];
          const pct = Math.round((count / total) * 100);
          return (
            <div key={type} className="bh2-etype-row">
              <span className="bh2-etype-dot" style={{ background: cfg?.color || '#94a3b8' }} />
              <span className="bh2-etype-label">{cfg?.label || type}</span>
              <div className="bh2-etype-bar-wrap">
                <div className="bh2-etype-bar" style={{ width: `${pct}%`, background: cfg?.color || '#94a3b8' }} />
              </div>
              <span className="bh2-etype-count">{count}</span>
            </div>
          );
        })
      }
    </Card>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const BatchHistory: React.FC = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const dispatch  = useDispatch();

  const batchId   = location.state?.batchId;
  const batchName = location.state?.batchName;

  const [search, setSearch]       = useState('');
  const [activeFilter, setActive] = useState('all');

  const batchHistoryState = useSelector((state: any) => state.getBatchHistoryReducer);

  useEffect(() => {
    if (batchId) dispatch(batchActions.getBatchHistory({ batchId, pageNumber: 1, pageSize: 100 }));
  }, [batchId, dispatch]);

  const goBack = () => navigate('/batches/view', { state: { batchId } });

  const historyData   = batchHistoryState?.historyData;
  const historyList: any[] = historyData?.history || [];
  const isLoading     = Boolean(batchHistoryState?.isLoading);

  const filtered = useMemo(() => {
    let list = [...historyList];
    if (activeFilter !== 'all') {
      list = list.filter((r) => (EVENT_CONFIG[r.type]?.category || 'other') === activeFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) => {
        const name  = getInstructorName(r)?.toLowerCase() || '';
        const label = EVENT_CONFIG[r.type]?.label.toLowerCase() || '';
        return name.includes(q) || label.includes(q);
      });
    }
    return list;
  }, [historyList, activeFilter, search]);

  if (!batchId) {
    return (
      <div className="batch-view-page">
        <div className="batch-view-header">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/batches')} className="batch-back-btn"></Button>
        </div>
        <Empty description="No batch context found" style={{ marginTop: 40 }} />
      </div>
    );
  }

  return (
    <div className="batch-view-page bh2-page">
      {/* ── Header ── */}
      <div className="batch-view-header">
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={goBack} className="batch-back-btn">
          {batchName || 'Batch'}
        </Button>
      </div>

      {/* ── Page title ── */}
      <div className="bh2-page-header">
        <div className="bh2-page-icon"><HistoryOutlined /></div>
        <div>
          <Title level={5} className="bh2-page-title">Batch Activity History</Title>
          <Text type="secondary" className="bh2-page-sub">Complete audit log of all batch activities</Text>
        </div>
      </div>

      {/* ── Top bar: filters + search ── */}
      <div className="bh2-topbar">
        <div className="bh2-filter-chips">
          {FILTER_CHIPS.map((chip) => (
            <button
              key={chip.key}
              className={`bh2-chip ${activeFilter === chip.key ? 'bh2-chip--active' : ''}`}
              onClick={() => setActive(chip.key)}
            >
              {chip.label}
            </button>
          ))}
        </div>
        <Input
          placeholder="Search events or instructors…"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bh2-search"
          allowClear
        />
      </div>

      {/* ── Body: timeline + sidebar ── */}
      <div className="bh2-body">

        {/* LEFT: Timeline */}
        <div className="bh2-timeline-col">
          {isLoading ? (
            <div className="bh2-loading"><Spin size="large" /></div>
          ) : filtered.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No activity records found" style={{ margin: '48px 0' }} />
          ) : (
            <div className="bh2-timeline">
              {filtered.map((record, idx) => (
                <TimelineItem
                  key={record._id || idx}
                  record={record}
                  isLast={idx === filtered.length - 1}
                />
              ))}
            </div>
          )}
        </div>

        {/* RIGHT: Sidebar */}
        <div className="bh2-sidebar">
          <SummaryCard    list={historyList} />
          <ActorsCard     list={historyList} />
          <EventTypesCard list={historyList} />
        </div>
      </div>
    </div>
  );
};

export default BatchHistory;